/*
 * PRECOMMANDE — cart guard (ticket 5595b980, 2026-10-17). MAUKAU.
 *
 * Makes every preorder line in the cart carry the « Pré-commande » property, whichever button or app put it
 * there. The decision is made server-side by snippets/preorder-cart-guard.liquid (which reads the same
 * snippets/preorder-date.liquid as the product page); this file only APPLIES the fixes it lists.
 *
 * When it runs:
 *   - on every page load, from the JSON the layout rendered with the page (no extra request when the cart
 *     is already right — the usual case);
 *   - after ANY request that changes the cart (/cart/add, /cart/change, /cart/update, /cart/clear), made by
 *     the theme or by an app. Those are seen through a PerformanceObserver on resource timings, so no
 *     app's fetch/XHR is wrapped or delayed; the guard then fetches /cart?section_id=preorder-cart-guard.
 *
 * Its own /cart/change.js calls are seen by the observer too; that costs one extra check which finds
 * nothing to do. It never throws into the page and never blocks the customer: any failure leaves the cart
 * exactly as it was.
 *
 * NOT COVERED: paths that never load a page of this theme before the checkout — cart permalinks
 * (/cart/<variant>:<qty>) and a checkout opened within a split second of an app's add, before the fix
 * lands.
 */
(() => {
  if (window.maukauPreorderCartGuard) return;
  window.maukauPreorderCartGuard = true;

  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const SECTION_ID = 'preorder-cart-guard';
  const CART_MUTATION = /\/cart\/(add|change|update|clear)(\.js|\.json)?$/;
  const MAX_FIXES_PER_RUN = 10;

  let running = false;
  let pending = false;
  let timer = null;

  const readFixes = (scope) => {
    const node = scope && scope.querySelector('script[data-preorder-cart-guard]');
    if (!node) return [];
    try {
      const fixes = JSON.parse(node.textContent);
      return Array.isArray(fixes) ? fixes : [];
    } catch (error) {
      return [];
    }
  };

  const fetchFixes = async () => {
    const response = await fetch(`${root}cart?section_id=${SECTION_ID}`, { credentials: 'same-origin', cache: 'no-store' });
    if (!response.ok) return [];
    return readFixes(new DOMParser().parseFromString(await response.text(), 'text/html'));
  };

  // The theme's own pattern (line-item-quantity): ask the cart surfaces which sections they need, change
  // the line, then announce the new cart so the drawer re-renders with the property shown.
  const applyFix = async (fix) => {
    const sections = [];
    document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections } }));
    if (!sections.includes(SECTION_ID)) sections.push(SECTION_ID);

    const response = await fetch(`${root}cart/change.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: fix.key, quantity: fix.quantity, properties: fix.properties, sections })
    });
    if (!response.ok) return null;
    return response.json();
  };

  // On the cart page the theme itself reloads after a line change; do the same, but only once the cart is
  // settled (nothing left to fix), so a fix that does not stick can never turn into a reload loop.
  const announce = (cart, settled) => {
    if (window.themeVariables && window.themeVariables.settings && window.themeVariables.settings.pageType === 'cart') {
      if (settled) window.location.reload();
      return;
    }
    document.documentElement.dispatchEvent(new CustomEvent('cart:change', { bubbles: true, detail: { baseEvent: 'preorder:sync', cart } }));
  };

  const run = async (initialFixes) => {
    if (running) {
      pending = true;
      return;
    }
    running = true;
    let lastCart = null;
    let fixes = [];

    try {
      fixes = initialFixes || (await fetchFixes());
      const tried = new Set();

      for (let i = 0; i < MAX_FIXES_PER_RUN && fixes.length; i++) {
        const fix = fixes.find((candidate) => !tried.has(candidate.key));
        if (!fix) break;
        tried.add(fix.key);

        const cart = await applyFix(fix);
        if (!cart) continue;
        lastCart = cart;

        const sectionHtml = cart.sections && cart.sections[SECTION_ID];
        fixes = sectionHtml ? readFixes(new DOMParser().parseFromString(sectionHtml, 'text/html')) : await fetchFixes();
      }
    } catch (error) {
      // Never let the guard break the page; the next cart change or page load tries again.
    }

    running = false;
    if (lastCart) announce(lastCart, fixes.length === 0);

    if (pending) {
      pending = false;
      schedule();
    }
  };

  const schedule = () => {
    clearTimeout(timer);
    timer = setTimeout(() => run(), 150);
  };

  if ('PerformanceObserver' in window) {
    try {
      new PerformanceObserver((list) => {
        const touchesCart = list.getEntries().some((entry) => {
          if (entry.initiatorType !== 'fetch' && entry.initiatorType !== 'xmlhttprequest') return false;
          try {
            return CART_MUTATION.test(new URL(entry.name, window.location.href).pathname);
          } catch (error) {
            return false;
          }
        });
        if (touchesCart) schedule();
      }).observe({ type: 'resource' });
    } catch (error) {
      // Old browser: the page-load check below still runs.
    }
  }

  window.addEventListener('pageshow', (event) => {
    if (event.persisted) schedule();
  });

  const initialFixes = readFixes(document);
  if (initialFixes.length) run(initialFixes);
})();
